<script setup lang="ts">
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { type PillTabOption, PillTabs } from '@/components/ui/pill-tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { formatDuration, formatShortDateTime } from '@/lib/commonFunctions';
import {
    fetchSlideEvalImage,
    listSlideEvals,
    type SlideEvalItem,
    type SlideEvalSlide
} from '@/services/slideEvals';
import { useTenantStore } from '@/stores/tenant';

const SIDES = ['source', 'rendered'] as const;
type Side = (typeof SIDES)[number];

type RenderState =
    | { status: 'loading' }
    | { status: 'failed'; reason: string }
    | { status: 'ready'; url: string };

// The stored side names say what a render *is* — what the conversion started from,
// what it produced — which holds whichever way the conversion runs. Only the human
// labels differ, and an unknown kind falls back to naming the roles.
const SIDE_LABELS: Record<string, Record<Side, string>> = {
    import: {
        source: 'Source deck (.pptx)',
        rendered: 'Imported slide (HTML)'
    },
    export: {
        source: 'Studio slide (HTML)',
        rendered: 'Exported deck (.pptx)'
    }
};
// One pair per kind from the project palette; an unknown kind stays neutral rather
// than borrowing a colour that means something else.
const KIND_CHIPS: Record<string, string> = {
    import: 'bg-blue25 text-blue100',
    export: 'bg-yellow25 text-yellow100'
};
const NEUTRAL_CHIP = 'bg-muted text-muted-foreground';

const ROLE_LABELS: Record<Side, string> = {
    source: 'Reference',
    rendered: 'Candidate'
};

function sideLabel(item: SlideEvalItem, side: Side): string {
    return (SIDE_LABELS[item.kind ?? ''] ?? ROLE_LABELS)[side];
}

const tenantStore = useTenantStore();

const PAGE_SIZE = 50;

const evals = ref<SlideEvalItem[]>([]);
const loading = ref(true);
const loadingMore = ref(false);
// A short page means the archive is exhausted: the backend pages over blobs, so a
// full page is the only evidence that another one exists.
const hasMore = ref(false);
const loadError = ref<string | null>(null);
const expandedEvalId = ref<string | null>(null);
const expandedSlideIndex = ref<number | null>(null);
// State of each render, keyed "<eval>:<side>:<index>". A key absent from the map
// has not been asked for yet, which is not the same answer as "there is none".
const renders = ref<Record<string, RenderState>>({});
// Bumped every time renders are dropped: a fetch started before the bump belongs to a
// view that is gone, and must not write over the entry a later fetch owns.
let generation = 0;
const zoomed = ref<{ item: SlideEvalItem; slide: SlideEvalSlide } | null>(null);

// Object URLs live until revoked; an admin paging through the archive would otherwise
// accumulate every render they ever expanded.
function releaseRenders(prefix?: string) {
    for (const [key, render] of Object.entries(renders.value)) {
        if (prefix && !key.startsWith(prefix)) continue;
        if (render.status === 'ready') URL.revokeObjectURL(render.url);
        delete renders.value[key];
    }
    generation++;
}

onUnmounted(() => releaseRenders());

function collapse() {
    if (expandedEvalId.value) releaseRenders(`${expandedEvalId.value}:`);
    expandedEvalId.value = null;
    expandedSlideIndex.value = null;
}

async function loadEvals() {
    // Collapse first: the refreshed list may not even contain the open row, and its
    // renders are about to be revoked.
    collapse();
    zoomed.value = null;
    loading.value = true;
    loadError.value = null;
    try {
        // The caller's own tenant (Elqano) — required by the auth gate, not a
        // result filter: the listing spans all tenants.
        evals.value = await listSlideEvals(
            tenantStore.tenantId || '',
            PAGE_SIZE
        );
        hasMore.value = evals.value.length === PAGE_SIZE;
        // The first page is a window on the most recent evals, so the filtered kind
        // can fall out of it. Its tabs go with it, leaving no way back to an empty page.
        if (!evals.value.some((item) => item.kind === kindFilter.value)) {
            kindFilter.value = ALL_KINDS;
        }
    } catch (error) {
        console.error(error);
        loadError.value = 'Failed to load evaluations';
    }
    loading.value = false;
}

// Appends, so the rows already open keep their renders.
async function loadMore() {
    loadingMore.value = true;
    loadError.value = null;
    try {
        const page = await listSlideEvals(
            tenantStore.tenantId || '',
            PAGE_SIZE,
            evals.value.length
        );
        evals.value = [...evals.value, ...page];
        hasMore.value = page.length === PAGE_SIZE;
    } catch (error) {
        console.error(error);
        loadError.value = 'Failed to load more evaluations';
    }
    loadingMore.value = false;
}

onMounted(loadEvals);

const ALL_KINDS = 'all';
const kindFilter = ref<string>(ALL_KINDS);

// Derived from what was loaded, so a kind we start recording later needs no change here.
const kindTabs = computed<PillTabOption[]>(() => {
    const kinds = evals.value
        .map((item) => item.kind)
        .filter((kind): kind is string => Boolean(kind));
    return [ALL_KINDS, ...[...new Set(kinds)].sort()].map((kind) => ({
        value: kind,
        label: kind.charAt(0).toUpperCase() + kind.slice(1)
    }));
});

const visibleEvals = computed(() =>
    kindFilter.value === ALL_KINDS
        ? evals.value
        : evals.value.filter((item) => item.kind === kindFilter.value)
);

const hasRows = computed(() => evals.value.length > 0);

function chipClass(kind: string | null): string {
    return KIND_CHIPS[kind ?? ''] ?? NEUTRAL_CHIP;
}

function filterBy(kind: string) {
    // The open row may not survive the filter, and its renders would leak with it.
    collapse();
    kindFilter.value = kind;
}

const PASSING_SCORE = 85;

function scoreClass(score: number | null | undefined): string {
    if (score == null) return 'text-muted-foreground';
    return score >= PASSING_SCORE ? 'text-green100' : 'text-destructive';
}

function slideCounts(item: SlideEvalItem): string {
    if (item.source_slide_count == null) return '—';
    const mismatch = item.source_slide_count !== item.rendered_slide_count;
    const deck = mismatch
        ? `${item.source_slide_count}→${item.rendered_slide_count}`
        : `${item.source_slide_count}`;
    return `${item.evaluated_count ?? 0}/${deck}`;
}

function imageKey(item: SlideEvalItem, side: Side, index: number): string {
    return `${item.eval_id}:${side}:${index}`;
}

function imageUrl(
    item: SlideEvalItem,
    side: Side,
    index: number
): string | undefined {
    const render = renders.value[imageKey(item, side, index)];
    return render?.status === 'ready' ? render.url : undefined;
}

// The reason the fetch failed, or undefined while it is still on its way. A render
// we have not resolved yet is not a render that does not exist.
function renderFailure(
    item: SlideEvalItem,
    side: Side,
    index: number
): string | undefined {
    const render = renders.value[imageKey(item, side, index)];
    return render?.status === 'failed' ? render.reason : undefined;
}

async function loadImage(item: SlideEvalItem, side: Side, index: number) {
    const key = imageKey(item, side, index);
    if (renders.value[key]) return;
    const requested = generation;
    renders.value[key] = { status: 'loading' };
    try {
        const url = await fetchSlideEvalImage(
            tenantStore.tenantId || '',
            item,
            side,
            index
        );
        // Collapsed or refreshed while this was in flight: nobody will revoke this URL
        // for us, and nothing is waiting for it.
        if (generation !== requested) {
            URL.revokeObjectURL(url);
            return;
        }
        renders.value[key] = { status: 'ready', url };
    } catch (error) {
        console.error(error);
        if (generation !== requested) return;
        renders.value[key] = {
            status: 'failed',
            reason: error instanceof Error ? error.message : String(error)
        };
    }
}

function toggleExpanded(item: SlideEvalItem) {
    const wasExpanded = expandedEvalId.value === item.eval_id;
    collapse();
    if (wasExpanded) return;
    expandedEvalId.value = item.eval_id;
}

// Renders are fetched when a slide is opened, not when the deck is: a 20-slide deck
// would otherwise cost 40 image requests nobody asked for.
function toggleSlide(item: SlideEvalItem, slide: SlideEvalSlide) {
    if (expandedSlideIndex.value === slide.index) {
        expandedSlideIndex.value = null;
        return;
    }
    expandedSlideIndex.value = slide.index;
    for (const side of SIDES) loadImage(item, side, slide.index);
}

function criteriaSummary(slide: SlideEvalSlide): string {
    return Object.entries(slide.criteria || {})
        .filter(([, score]) => score != null)
        .map(([name, score]) => `${name} ${score}`)
        .join(' · ');
}
</script>

<template>
    <div class="flex w-full min-w-0 flex-col gap-4">
        <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
                <h2 class="text-xl font-semibold">Quality score</h2>
                <p class="text-sm text-muted-foreground">
                    How faithfully decks survive a conversion, across all
                    workspaces. <br />

                    Up to 20 slides per deck, archived 30 days.
                </p>
            </div>
            <Button variant="outline" :disabled="loading" @click="loadEvals">
                <RefreshCw class="mr-2 h-4 w-4" />
                Refresh
            </Button>
        </div>

        <PillTabs
            v-if="!loading && !loadError && kindTabs.length > 2"
            :model-value="kindFilter"
            :tabs="kindTabs"
            @update:model-value="filterBy(String($event))"
        />

        <div v-if="loading" class="flex justify-center py-16">
            <Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
        </div>

        <p
            v-else-if="loadError && !hasRows"
            class="py-8 text-center text-destructive"
        >
            {{ loadError }}
        </p>

        <p v-else-if="!hasRows" class="py-8 text-center text-muted-foreground">
            No evaluations recorded yet.
        </p>

        <p
            v-else-if="!visibleEvals.length"
            class="py-8 text-center text-muted-foreground"
        >
            No {{ kindFilter }} evaluations in the pages loaded so far.
        </p>

        <Table
            v-else
            class="table-fixed"
            container-class="w-full overflow-x-hidden"
        >
            <TableHeader>
                <TableRow>
                    <TableHead class="w-7 px-1" />
                    <TableHead class="w-44">Date</TableHead>
                    <TableHead class="w-24">Step</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead class="w-16 text-right">Score</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <template v-for="item in visibleEvals" :key="item.eval_id">
                    <TableRow
                        class="cursor-pointer"
                        @click="toggleExpanded(item)"
                    >
                        <TableCell class="px-1 text-muted-foreground">
                            <ChevronDown
                                v-if="expandedEvalId === item.eval_id"
                                class="h-4 w-4"
                            />
                            <ChevronRight v-else class="h-4 w-4" />
                        </TableCell>
                        <TableCell
                            class="whitespace-nowrap"
                            :title="item.created_at || undefined"
                        >
                            {{ formatShortDateTime(item.created_at) }}
                        </TableCell>
                        <TableCell>
                            <span
                                class="rounded-full px-2 py-0.5 text-xs font-medium capitalize"
                                :class="chipClass(item.kind)"
                            >
                                {{ item.kind || 'unknown' }}
                            </span>
                        </TableCell>
                        <TableCell
                            class="truncate"
                            :title="item.tenant_name || item.tenant_id || ''"
                        >
                            {{ item.tenant_name || item.tenant_id || '—' }}
                        </TableCell>
                        <TableCell
                            class="text-right font-semibold"
                            :class="scoreClass(item.deck_score)"
                        >
                            {{ item.deck_score ?? '—' }}
                        </TableCell>
                    </TableRow>
                    <TableRow v-if="expandedEvalId === item.eval_id">
                        <TableCell />
                        <TableCell colspan="4" class="bg-muted/40">
                            <div
                                class="flex min-w-0 flex-col gap-4 py-2 text-sm"
                            >
                                <dl
                                    class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs"
                                >
                                    <dt class="text-muted-foreground">
                                        Evaluation status
                                    </dt>
                                    <dd>
                                        <span
                                            class="rounded-full px-2 py-0.5 text-xs font-medium"
                                            :class="
                                                item.status === 'SUCCESS'
                                                    ? 'bg-green25 text-green100'
                                                    : 'bg-destructive/10 text-destructive'
                                            "
                                        >
                                            {{ item.status || 'UNKNOWN' }}
                                        </span>
                                    </dd>
                                    <dt
                                        class="text-muted-foreground"
                                        title="Slides graded / slides in the deck"
                                    >
                                        Slides graded
                                    </dt>
                                    <dd class="tabular-nums">
                                        {{ slideCounts(item) }}
                                    </dd>
                                    <dt class="text-muted-foreground">
                                        Document
                                    </dt>
                                    <dd class="select-all break-all font-mono">
                                        {{ item.document_id || '—' }}
                                    </dd>
                                    <dt class="text-muted-foreground">
                                        Evaluation
                                    </dt>
                                    <dd class="select-all break-all font-mono">
                                        {{ item.eval_id }} ({{ item.kind }})
                                    </dd>
                                    <dt class="text-muted-foreground">Judge</dt>
                                    <dd class="break-all font-mono">
                                        {{ item.model || '?' }} —
                                        {{ formatDuration(item.duration_s) }}
                                    </dd>
                                    <dt
                                        class="text-muted-foreground"
                                        title="The engine that drew each side. Neither is PowerPoint, so some differences are the renderer's, not the conversion's."
                                    >
                                        Renderers
                                    </dt>
                                    <dd class="break-all font-mono">
                                        {{ item.reference_renderer || '?' }}
                                        →
                                        {{ item.candidate_renderer || '?' }}
                                    </dd>
                                </dl>
                                <p
                                    v-if="item.error"
                                    class="whitespace-pre-wrap break-words font-mono text-xs text-destructive"
                                >
                                    {{ item.error }}
                                </p>
                                <div
                                    v-for="slide in item.slides"
                                    :key="slide.index"
                                    class="flex min-w-0 flex-col gap-2 border-t pt-3"
                                >
                                    <div
                                        class="flex cursor-pointer flex-wrap items-center gap-x-3"
                                        @click="toggleSlide(item, slide)"
                                    >
                                        <ChevronDown
                                            v-if="
                                                expandedSlideIndex ===
                                                slide.index
                                            "
                                            class="h-4 w-4 text-muted-foreground"
                                        />
                                        <ChevronRight
                                            v-else
                                            class="h-4 w-4 text-muted-foreground"
                                        />
                                        <span class="font-semibold">
                                            Slide {{ slide.index }}
                                        </span>
                                        <span
                                            class="font-semibold"
                                            :class="scoreClass(slide.score)"
                                        >
                                            {{ slide.score ?? slide.error }}
                                        </span>
                                        <span
                                            class="font-mono text-xs text-muted-foreground"
                                        >
                                            {{ criteriaSummary(slide) }}
                                        </span>
                                    </div>
                                    <template
                                        v-if="
                                            expandedSlideIndex === slide.index
                                        "
                                    >
                                        <div class="grid grid-cols-2 gap-3">
                                            <figure
                                                v-for="side in SIDES"
                                                :key="side"
                                                class="flex min-w-0 flex-col gap-1"
                                            >
                                                <figcaption
                                                    class="truncate text-xs text-muted-foreground"
                                                >
                                                    {{ sideLabel(item, side) }}
                                                </figcaption>
                                                <img
                                                    v-if="
                                                        imageUrl(
                                                            item,
                                                            side,
                                                            slide.index
                                                        )
                                                    "
                                                    :src="
                                                        imageUrl(
                                                            item,
                                                            side,
                                                            slide.index
                                                        )
                                                    "
                                                    :alt="`${side} render of slide ${slide.index}`"
                                                    class="w-full cursor-zoom-in rounded border"
                                                    @click.stop="
                                                        zoomed = {
                                                            item,
                                                            slide
                                                        }
                                                    "
                                                />
                                                <div
                                                    v-else
                                                    class="flex aspect-video items-center justify-center rounded border text-xs text-muted-foreground"
                                                >
                                                    <Loader2
                                                        v-if="
                                                            !renderFailure(
                                                                item,
                                                                side,
                                                                slide.index
                                                            )
                                                        "
                                                        class="h-4 w-4 animate-spin"
                                                    />
                                                    <span
                                                        v-else
                                                        :title="
                                                            renderFailure(
                                                                item,
                                                                side,
                                                                slide.index
                                                            )
                                                        "
                                                    >
                                                        Failed to load
                                                    </span>
                                                </div>
                                            </figure>
                                        </div>
                                        <ul
                                            v-if="slide.issues?.length"
                                            class="flex list-disc flex-col gap-1 pl-5"
                                        >
                                            <li
                                                v-for="(
                                                    issue, i
                                                ) in slide.issues"
                                                :key="i"
                                                class="break-words font-mono text-xs"
                                            >
                                                <span class="font-semibold">
                                                    {{ issue.criterion }} /
                                                    {{ issue.severity }}
                                                </span>
                                                — {{ issue.description }}
                                                <span
                                                    v-if="
                                                        issue.likely_renderer_artifact
                                                    "
                                                    class="text-amber-700"
                                                >
                                                    (likely a render artifact)
                                                </span>
                                            </li>
                                        </ul>
                                    </template>
                                </div>
                            </div>
                        </TableCell>
                    </TableRow>
                </template>
            </TableBody>
        </Table>

        <p
            v-if="loadError && hasRows"
            class="text-center text-sm text-destructive"
        >
            {{ loadError }}
        </p>

        <div v-if="hasMore && !loading" class="flex justify-center">
            <Button variant="outline" :disabled="loadingMore" @click="loadMore">
                <Loader2 v-if="loadingMore" class="mr-2 h-4 w-4 animate-spin" />
                Load more
            </Button>
        </div>

        <Dialog
            :open="zoomed !== null"
            @update:open="(open) => !open && (zoomed = null)"
        >
            <DialogContent class="max-w-[95vw]">
                <DialogTitle class="text-base">
                    Slide {{ zoomed?.slide.index }} —
                    <span :class="scoreClass(zoomed?.slide.score)">
                        {{ zoomed?.slide.score ?? '—' }}
                    </span>
                </DialogTitle>
                <div v-if="zoomed" class="grid grid-cols-2 gap-4">
                    <figure
                        v-for="side in SIDES"
                        :key="side"
                        class="flex min-w-0 flex-col gap-1"
                    >
                        <figcaption class="text-xs text-muted-foreground">
                            {{ sideLabel(zoomed.item, side) }}
                        </figcaption>
                        <img
                            v-if="
                                imageUrl(zoomed.item, side, zoomed.slide.index)
                            "
                            :src="
                                imageUrl(zoomed.item, side, zoomed.slide.index)
                            "
                            :alt="`${side} render of slide ${zoomed.slide.index}`"
                            class="w-full rounded border"
                        />
                        <div
                            v-else
                            class="flex aspect-video items-center justify-center rounded border text-xs text-muted-foreground"
                        >
                            <Loader2
                                v-if="
                                    !renderFailure(
                                        zoomed.item,
                                        side,
                                        zoomed.slide.index
                                    )
                                "
                                class="h-4 w-4 animate-spin"
                            />
                            <span
                                v-else
                                :title="
                                    renderFailure(
                                        zoomed.item,
                                        side,
                                        zoomed.slide.index
                                    )
                                "
                            >
                                Failed to load
                            </span>
                        </div>
                    </figure>
                </div>
            </DialogContent>
        </Dialog>
    </div>
</template>
